import { apiFetch, httpJson } from "./client";

export async function getGrowthOverview() {
  return await httpJson("/growth/overview", { method: "GET" });
}

export async function getGrowthLearning() {
  return await httpJson("/growth/learning", { method: "GET" });
}

export async function getGrowthAudit(filters = {}) {
  const params = new URLSearchParams();
  if (filters.entity_type) params.set("entity_type", String(filters.entity_type));
  if (filters.action) params.set("action", String(filters.action));
  if (filters.platform) params.set("platform", String(filters.platform));
  if (filters.limit) params.set("limit", String(filters.limit));
  const query = params.toString();
  return await httpJson(query ? `/growth/audit?${query}` : "/growth/audit", { method: "GET" });
}

export async function getGrowthAgents() {
  return await httpJson("/growth/agents", { method: "GET" });
}

export async function runGrowthAgents() {
  return await httpJson("/growth/agents/run", { method: "POST" });
}

export async function listGrowthInboxMessages() {
  const data = await httpJson("/growth/inbox/messages", { method: "GET" });
  return Array.isArray(data?.messages) ? data.messages : [];
}

export async function listGrowthTargets() {
  const data = await httpJson("/growth/targets", { method: "GET" });
  return Array.isArray(data?.targets) ? data.targets : [];
}

export async function createGrowthTarget(payload) {
  return await httpJson("/growth/targets", {
    method: "POST",
    body: payload || {},
  });
}

export async function updateGrowthTarget(targetId, payload) {
  const id = String(targetId || "").trim();
  if (!id) throw new Error("targetId is required");
  return await httpJson(`/growth/targets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: payload || {},
  });
}

export async function runGrowthDiscoveryIntake(payload) {
  const next = payload || {};
  const entries = String(next.entries || "").trim();
  if (!entries) throw new Error("Discovery intake requires at least one URL or handle.");
  return await httpJson("/growth/discovery-intake", {
    method: "POST",
    body: { ...next, entries },
  });
}

export async function listGrowthActions() {
  const data = await httpJson("/growth/actions", { method: "GET" });
  return Array.isArray(data?.actions) ? data.actions : [];
}

export async function listGrowthContentAssets() {
  const data = await httpJson("/growth/content-assets", { method: "GET" });
  return Array.isArray(data?.assets) ? data.assets : [];
}

export async function uploadGrowthContentAssets({ files, notes = "" } = {}) {
  const nextFiles = Array.from(files || []).filter(Boolean);
  if (!nextFiles.length) {
    throw new Error("At least one media file is required.");
  }
  const body = new FormData();
  nextFiles.forEach((file) => body.append("files", file));
  body.append("notes", String(notes || "").trim());
  const { data } = await apiFetch("/growth/content-assets/upload", {
    method: "POST",
    body,
  });
  return Array.isArray(data?.assets) ? data.assets : [];
}

export async function deleteGrowthContentAsset(assetId) {
  const id = String(assetId || "").trim();
  if (!id) throw new Error("assetId is required");
  return await httpJson(`/growth/content-assets/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function createGrowthAction(payload) {
  return await httpJson("/growth/actions", {
    method: "POST",
    body: payload || {},
  });
}

export async function deleteGrowthAction(actionId) {
  const id = String(actionId || "").trim();
  if (!id) throw new Error("actionId is required");
  return await httpJson(`/growth/actions/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function rewriteGrowthAction(actionId, payload) {
  const id = String(actionId || "").trim();
  if (!id) throw new Error("actionId is required");
  return await httpJson(`/growth/actions/${encodeURIComponent(id)}/rewrites`, {
    method: "POST",
    body: payload || {},
  });
}

export async function listGrowthPlatformAccounts() {
  const data = await httpJson("/growth/platform-accounts", { method: "GET" });
  return Array.isArray(data?.accounts) ? data.accounts : [];
}

export async function updateGrowthPlatformAccount(platform, payload) {
  const key = String(platform || "").trim();
  if (!key) throw new Error("platform is required");
  return await httpJson(`/growth/platform-accounts/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: payload || {},
  });
}

export async function syncGrowthBufferChannels() {
  return await httpJson("/growth/platform-accounts/sync-buffer", { method: "POST" });
}

export async function listGrowthFeedCandidates() {
  const data = await httpJson("/growth/feed-candidates", { method: "GET" });
  return Array.isArray(data?.feed_candidates) ? data.feed_candidates : [];
}

export async function rejectGrowthFeedCandidate(feedCandidateId, rejectedReason = "") {
  const id = String(feedCandidateId || "").trim();
  if (!id) throw new Error("feedCandidateId is required");
  return await httpJson(`/growth/feed-candidates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: {
      decision: "reject",
      rejected_reason: String(rejectedReason || "").trim() || null,
    },
  });
}

export async function runGrowthFeedIntake(payload) {
  const next = payload || {};
  const entries = String(next.entries || "").trim();
  if (!entries) throw new Error("Feed intake requires at least one post URL or text snippet.");
  return await httpJson("/growth/feed-candidates/intake", {
    method: "POST",
    body: { ...next, entries },
  });
}

export async function decideGrowthAction(actionId, payload) {
  const id = String(actionId || "").trim();
  if (!id) throw new Error("actionId is required");
  return await httpJson(`/growth/actions/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: payload || {},
  });
}

export async function sendGrowthAction(actionId, payload) {
  const id = String(actionId || "").trim();
  if (!id) throw new Error("actionId is required");
  return await httpJson(`/growth/actions/${encodeURIComponent(id)}/send`, {
    method: "POST",
    body: payload || {},
  });
}

export async function retryGrowthFailedActions(payload) {
  return await httpJson("/growth/actions/retry-failed", {
    method: "POST",
    body: payload || {},
  });
}
